import Store from './Store';
import Render from './Render';
import { InsideOptionType, DoorType, SlidingDoorsType, Side, } from '../types/types';
import { OUTER_DOOR_BACKGROUND } from "../types/Colors";
const fontSize = 14;
const DIMENSION_SPACING = 28;
const TOP_INSIDE_OPTION_OFFSET = 7.5;
const DRAWER_HEIGHT = 18;
const DRAWER_SPACE_BETWEEN_DESKS = 3;
const DRAWER_DOOR_SPACE = 5;
const PANTOGRAPH_SETTINGS = {
    leftOffset: 5,
    rodWidth: 1,
    height: 87,
    holderWidth: 7,
    holderHeight: 22,
};
export const SLIDING_DOOR_DEPTH_OFFSET = 0.8;
export const SLIDING_DOOR_BOTTOM_OFFSET = 3;
export const SLIDING_DOOR_TOP_COLUMN_OFFSET = 6;
export const SLIDING_DOOR_DESK_THICKNESS = 1.9;
const METAL_FRAME_1_STRUT_LIMIT = 120;
const METAL_FRAME_2_STRUT_LIMIT = 120;
/**
 * Render class
 * Rendering canvas by settings and data
 */
export default class MeasurementView {
    constructor(options, data) {
        this._defaultCanvasPadding = 10;
        this._withoutLabelsCanvasPadding = 26;
        let defaults = { showLabels: true, };
        this._options = Object.assign({}, defaults, options);
        this._render = new Render(options);
        this._store = new Store(data);
        this.setData(data);
        this._init();
    }
    get render() {
        return this._render;
    }
    get store() {
        return this._store;
    }
    get options() {
        return this._options;
    }
    get sizer() {
        return this._render.sizer;
    }
    get bevelAngle() {
        if (!this.store.isBevel) {
            return 0;
        }
        const firstBevel = this.store.firstBevelDeskIndex;
        const lastBevel = this.store.lastBevelDeskIndex;
        const width = this.store.isLeftBevel
            ? this.store.verticalDesks[lastBevel].leftOffset
            : (this.store.verticalDesks[firstBevel].leftOffset - this.store.verticalDesks[lastBevel].leftOffset);
        const height = this.store.verticalDesks[lastBevel].height - this.store.bevelHeight;
        return this._angle(0, 0, width, height);
    }
    get imgIconScaleSize() {
        return (this._fontSize * 1.5) / this.sizer;
    }
    /**
     * Return true for door view mode
     *
     * @param doorView
     */
    set doorView(doorView) {
        this._options.modelView = false;
        this._render.isModelView = false;
        this._options.innerView = false;
        this._render.isInnerView = false;
        this._options.doorView = doorView;
        this._render.isDoorView = doorView;
        this._init();
    }
    /**
     * Return true for model view mode
     *
     * @param modelView
     */
    set modelView(modelView) {
        this._options.doorView = false;
        this._render.isDoorView = false;
        this._options.innerView = false;
        this._render.isInnerView = false;
        this._options.modelView = modelView;
        this._render.isModelView = modelView;
        this._init();
    }
    /**
     * Return true for inner view mode
     *
     * @param innerView
     */
    set innerView(innerView) {
        this._options.doorView = false;
        this._render.isDoorView = false;
        this._options.modelView = false;
        this._render.isModelView = false;
        this._options.innerView = innerView;
        this._render.isInnerView = innerView;
        this._init();
    }
    get showLabels() {
        return this._options.showLabels;
    }
    set showLabels(val) {
        this._options.showLabels = val;
        this._init();
    }
    get clientHeight() {
        return this._options.canvas.clientHeight;
    }
    get clientWidth() {
        return this._options.canvas.clientWidth;
    }
    get _canvasPadding() {
        return !this.showLabels ? this._withoutLabelsCanvasPadding : this._defaultCanvasPadding;
    }
    get _resizeIncrement() {
        let totalHeight = 2 * this._canvasPadding;
        totalHeight = totalHeight + this._allHorizontalDimensionsSize;
        if (this.store.hasCoverStrip) {
            totalHeight += (this.store.coverstrip.longVerticalDeskSize - this.store.height);
        }
        const diffY = (this.options.canvas.clientHeight - totalHeight) / this.store.height;
        let totalWidth = 2 * this._canvasPadding;
        totalWidth += this._allVerticalDimensionsSize;
        if (this.store.hasCoverStrip || this.store.outerDoors.length > 0) {
            totalWidth += this.store.outerDoorOrDeskOffset;
        }
        const diffX = (this.options.canvas.clientWidth - totalWidth) / this._modelWidth;
        return Math.min(diffX, diffY);
    }
    get _topCenter() {
        let topCenter = ((this.clientHeight - (this._allHorizontalDimensionsSize + (this.sizer * this.store.height))) / 2);
        topCenter = (topCenter > 0 ? topCenter : 0) + this._canvasPadding;
        if (this.store.hasCoverStrip) {
            topCenter += (this.store.coverstrip.longVerticalDeskSize - this.store.height) * this.sizer;
        }
        if (!this.isModelView && !this.isDoorView && !this.isInnerView) {
            return topCenter;
        }
        else {
            topCenter = topCenter + this._fontSize;
        }
        // top offset for object
        return topCenter;
    }
    get _leftCenter() {
        if (this.isModelView || this.isDoorView || this.isInnerView) {
            return (this.clientWidth - (this._modelWidth * this.sizer)) / 2;
        }
        let leftCenter = ((this.clientWidth - (this._allVerticalDimensionsSize + (this._modelWidth * this.sizer)) + 2 * this._canvasPadding)) / 2;
        if (this.store.hasCoverStrip || this.store.outerDoors.length > 0) {
            leftCenter += (this.store.outerDoorOrDeskOffset + this.sizer) / 2;
        }
        if (this.store.hasCoverStrip) {
            leftCenter += this.store.coverstrip.thickness * this.sizer;
        }
        return leftCenter;
    }
    get _modelWidth() {
        if (this.isModelView || this.isDoorView || this.isInnerView) {
            return this.store.width;
        }
        return this.store.width + this.store.depth + this.store.outerDoorOrDeskOffset;
    }
    get sinus() {
        return Math.sin((180 - this.bevelAngle - 90) * (Math.PI / 180));
    }
    get _fontSize() {
        return this._dimensionSpacing / DIMENSION_SPACING > 1
            ? fontSize
            : fontSize * this._dimensionSpacing / DIMENSION_SPACING;
    }
    get _dimensionSpacing() {
        return ((this.clientWidth - DIMENSION_SPACING * this._allVerticalDimensions)) > 0.35
            ? (this.clientWidth * 0.25) / this._allVerticalDimensions
            : DIMENSION_SPACING;
    }
    get _renderDimensionSpacing() {
        return this._dimensionSpacing / this.sizer;
    }
    get _leftDimensionsSpacingSize() {
        if (this.isModelView || this.isDoorView || this.isInnerView) {
            return 0;
        }
        // one more added for space between dimension and model
        return (this.store.leftDimensions.length + 1) * this._dimensionSpacing;
    }
    get _leftDimensionsSpacingRenderSize() {
        // one more added for space between dimension and model
        return this._leftDimensionsSpacingSize / this.sizer;
    }
    get _allHorizontalDimensionsSize() {
        if (this.isModelView || this.isDoorView || this.isInnerView) {
            if (this.store.hasSocket && !this.store.hasFeet) {
                if (this.store.hasHorizontalBottomSplits) {
                    return 6 * this._fontSize;
                }
                else {
                    return 5 * this._fontSize;
                }
            }
            if (this.store.hasHorizontalBottomSplits) {
                return 5 * this._fontSize;
            }
            return 4 * this._fontSize;
        }
        let dIncrement = this.store.outerDoorOffset > 0 ? 2 : 1;
        if (this.store.hasColumnsSameHeight) {
            if (this._store.hasHorizontalBottomSplits) {
                return (4 + dIncrement) * this._dimensionSpacing;
            }
            return (3 + dIncrement) * this._dimensionSpacing;
        }
        if (this.store.hasHorizontalBottomSplits) {
            return (5 + dIncrement) * this._dimensionSpacing;
        }
        return (4 + dIncrement) * this._dimensionSpacing;
    }
    get _allVerticalDimensionsSize() {
        if (this.isModelView || this.isDoorView || this.isInnerView) {
            return 0;
        }
        return this._allVerticalDimensions * this._dimensionSpacing;
    }
    get _allVerticalDimensions() {
        return this.summary([
            (this.store.leftDimensions.length + 2),
            this._sideViewDimensionCount,
            this._coverStripDimension,
        ]);
    }
    get _sideViewDimensionCount() {
        if (this.isModelView || this.isDoorView || this.isInnerView) {
            return 0;
        }
        let dimensionCount = (this.store.hasSkirtingBoard ? 3 : 2);
        if (this.store.hasCoverStrip) {
            dimensionCount += 1;
        }
        return dimensionCount;
    }
    get _sideViewDimensionSpacingSize() {
        return this._sideViewDimensionCount * this._dimensionSpacing;
    }
    get _sideViewDimensionSpacingRenderSize() {
        return this._sideViewDimensionSpacingSize / this.sizer;
    }
    get _coverStripDimension() {
        return (this.store.hasCoverStrip) ? 1 : 0;
    }
    get isModelView() {
        return this._options.modelView !== undefined ? this._options.modelView : false;
    }
    get isDoorView() {
        return this._options.doorView !== undefined ? this._options.doorView : false;
    }
    get isInnerView() {
        return this._options.innerView !== undefined ? this._options.innerView : false;
    }
    get showSideView() {
        return !this.isModelView && !this.isDoorView && !this.isInnerView;
    }
    renderObject() {
        this.render.clear();
        if (!this.store.hasContinuousDesks) {
            this._renderBottomDesk();
            this._renderSocket();
            this._renderRegales();
        }
        if (!this.isModelView && !this.isDoorView && !this.isInnerView) {
            this._renderLeftDimensions();
            this._renderInnerNumbers();
            this._renderBottomDimensions();
        }
        if (this.isDoorView) {
            this._renderInnerNumbers();
        }
        if (this.store.isBevel) {
            this._renderBevel();
        }
        this._renderBevelCoverStrip();
        this._renderVerticalDesks();
        if (this.store.hasContinuousDesks) {
            this._renderBottomDesk();
            this._renderSocket();
            this._renderRegales();
        }
        if (this.showSideView) {
            this._renderSideView();
            if (!this.store.hasCoverStrip) {
                this._renderSideViewOuterDoors();
            }
            this._renderSideViewCoverStrip();
        }
        if (this.isDoorView) {
            this._renderDoorsView();
        }
        if (this.isInnerView) {
            this._renderInsideOptions();
            this._renderInnerNumbers();
        }
        this.render.renderAll();
    }
    _angle(cx, cy, ex, ey) {
        return (Math.atan2(ey - cy, ex - cx)) * 180 / Math.PI;
    }
    // set input object data
    setData(data) {
        this.store.setData(data);
        this._init();
    }
    setSize(width, height) {
        this.render.setCanvasSize(width, height);
        this._init();
        this.renderObject();
    }
    _init() {
        this._render.sizer = this._resizeIncrement;
        this._render.leftCenter = this._leftCenter;
        this._render.topCenter = this._topCenter;
        this._render.fontSize = this._fontSize;
        this._render.width = this.store.width;
    }
    // return arrow data
    arrow(top, left, direction) {
        const isVertical = direction === 'top' || direction === 'bottom';
        const isTop = direction === 'top';
        const isLeft = direction === 'left';
        let arrowSize = this._renderDimensionSpacing * 0.1;
        let arrowHeight = arrowSize * 1.5;
        let arrowWidth = arrowSize * 1.5;
        return {
            top: isVertical ? isTop ? top : top - arrowHeight : top - (arrowSize / 2),
            left: isVertical ? left - (arrowSize / 2) : isLeft ? left : (left - arrowWidth),
            backgroundColor: 'black',
            points: isVertical
                ? [
                    { x: 0, y: isTop ? arrowHeight : 0 },
                    { x: arrowSize, y: isTop ? arrowHeight : 0 },
                    { x: arrowSize / 2, y: isTop ? 0 : arrowHeight },
                ]
                : [
                    { x: isLeft ? 0 : arrowHeight, y: arrowSize / 2 },
                    { x: isLeft ? arrowHeight : 0, y: 0 },
                    { x: isLeft ? arrowHeight : 0, y: arrowSize },
                ],
        };
    }
    // return sum of values in array
    summary(values) {
        let sum = 0;
        values.forEach(val => sum = sum + val);
        return sum;
    }
    // render socket
    _renderSocket() {
        if (this.store.hasSocket && !this.store.hasFeet) {
            // render socket
            this._render.rectangle({
                top: this.store.height - this.store.socket.height,
                left: this._leftDimensionsSpacingRenderSize + (this.store.hasContinuousDesks ? this.store.aroundThickness : 0),
                width: this.store.width - (this.store.hasContinuousDesks ? 2 * this.store.aroundThickness : 0),
                height: this.store.socket.height,
            });
            // render socket split
            if (this.store.hasHorizontalSplits) {
                if (this.store.horizontalBottomSplits.length > 0) {
                    this.store.horizontalBottomSplits.forEach((split) => {
                        const splitLine = {
                            topY: this.store.height - this.store.socket.height,
                            bottomY: this.store.height,
                            topX: this._leftDimensionsSpacingRenderSize + split + this.store.aroundThickness,
                            bottomX: this._leftDimensionsSpacingRenderSize + split + this.store.aroundThickness,
                        };
                        this._render.line(splitLine);
                    });
                }
            }
        }
        else if (this.store.hasFeet) {
            const feetCount = this.store.feetData.count - 1;
            const feetTop = this.store.height - this.store.feetData.height;
            // render feet
            if (this.store.hasFeetRolls) {
                // left roll
                this._renderRoll({
                    top: feetTop,
                    left: this._leftDimensionsSpacingRenderSize,
                    width: this.store.feetData.width,
                    height: this.store.feetData.height,
                    sideView: false
                });
                for (let i = 1; i < feetCount; i++) {
                    this._renderRoll({
                        top: feetTop,
                        left: ((this.store.width / feetCount) * i) - (this.store.feetData.width / 2) + this._leftDimensionsSpacingRenderSize,
                        width: this.store.feetData.width,
                        height: this.store.feetData.height,
                        sideView: false
                    });
                }
                // right roll
                this._renderRoll({
                    top: feetTop,
                    left: this.store.width - this.store.feetData.width + this._leftDimensionsSpacingRenderSize,
                    width: this.store.feetData.width,
                    height: this.store.feetData.height,
                    sideView: false
                });
            }
            else if (this.store.hasFeetLongDesk) {
                this._render.rectangle({
                    top: feetTop,
                    left: ((this.store.width - this.store.feetData.width) / 2) + this._leftDimensionsSpacingRenderSize,
                    height: this.store.feetData.height,
                    width: this.store.feetData.width
                });
            }
            else if (this.store.hasFeetMetal5 || this.store.hasFeetMetal2) {
                // render left metal feet
                this._renderMetalFeet5(feetTop, this._leftDimensionsSpacingRenderSize, this.store.feetData.width, this.store.feetData.height, true);
                // render middle metal feet
                for (let i = 1; i < feetCount; i++) {
                    this._renderMetalFeet5(feetTop, this._leftDimensionsSpacingRenderSize + ((this.store.width / feetCount) * i) - (this.store.feetData.width / 2), this.store.feetData.width, this.store.feetData.height, this.store.feetData.count / 2 > i);
                }
                // render right metal feet
                this._renderMetalFeet5(feetTop, this._leftDimensionsSpacingRenderSize + (this.store.width - this.store.feetData.height), this.store.feetData.width, this.store.feetData.height, false);
            }
            else if (this.store.hasFeetWoodFrame) {
                // left
                this._renderWoodFrameFeet(feetTop, this._leftDimensionsSpacingRenderSize, true);
                // middle
                this._render.rectangle({
                    top: feetTop,
                    left: this._leftDimensionsSpacingRenderSize + this.store.feetData.width,
                    height: this.store.feetData.width,
                    width: this.store.width - (this.store.feetData.width * 2),
                });
                // right
                this._renderWoodFrameFeet(feetTop, this._leftDimensionsSpacingRenderSize + this.store.width - this.store.feetData.width, false);
            }
            else if (this.store.hasFeetMetalFrame1) {
                // left
                this._render.rectangle({
                    top: feetTop + this.store.feetData.width,
                    left: this._leftDimensionsSpacingRenderSize,
                    height: this.store.socket.height - this.store.feetData.width,
                    width: this.store.feetData.width,
                });
                // middle
                this._render.rectangle({
                    top: feetTop,
                    left: this._leftDimensionsSpacingRenderSize,
                    height: this.store.feetData.width,
                    width: this.store.width,
                });
                // right
                this._render.rectangle({
                    top: feetTop + this.store.feetData.width,
                    left: this._leftDimensionsSpacingRenderSize + this.store.width - this.store.feetData.width,
                    height: this.store.socket.height - this.store.feetData.width,
                    width: this.store.feetData.width,
                });
                // strut
                if (this.store.width > METAL_FRAME_1_STRUT_LIMIT) {
                    this._render.rectangle({
                        top: feetTop + this.store.feetData.width,
                        left: this._leftDimensionsSpacingRenderSize + (this.store.width - this.store.feetData.width) / 2,
                        height: this.store.socket.height - this.store.feetData.width,
                        width: this.store.feetData.width,
                    });
                }
            }
            else if (this.store.hasFeetMetalFrame2) {
                // left
                this._render.rectangle({
                    top: feetTop + this.store.feetData.width,
                    left: this._leftDimensionsSpacingRenderSize,
                    height: this.store.socket.height - (this.store.feetData.width * 2),
                    width: this.store.feetData.width,
                });
                // middle top
                this._render.rectangle({
                    top: feetTop,
                    left: this._leftDimensionsSpacingRenderSize,
                    height: this.store.feetData.width,
                    width: this.store.width,
                });
                // middle bottom
                this._render.rectangle({
                    top: feetTop + this.store.socket.height - this.store.feetData.width,
                    left: this._leftDimensionsSpacingRenderSize,
                    height: this.store.feetData.width,
                    width: this.store.width,
                });
                // right
                this._render.rectangle({
                    top: feetTop + this.store.feetData.width,
                    left: this._leftDimensionsSpacingRenderSize + this.store.width - this.store.feetData.width,
                    height: this.store.socket.height - (this.store.feetData.width * 2),
                    width: this.store.feetData.width,
                });
                // strut
                if (this.store.width > METAL_FRAME_2_STRUT_LIMIT) {
                    this._render.rectangle({
                        top: feetTop + this.store.feetData.width,
                        left: this._leftDimensionsSpacingRenderSize + (this.store.width - this.store.feetData.width) / 2,
                        height: this.store.socket.height - (this.store.feetData.width * 2),
                        width: this.store.feetData.width,
                    });
                }
            }
            else if (this.store.hasFeetHairpin) {
                // render left
                this._renderHairpin({
                    top: feetTop,
                    left: this._leftDimensionsSpacingRenderSize,
                    side: Side.Left,
                });
                // render middle
                for (let i = 1; i < feetCount; i++) {
                    this._renderHairpin({
                        top: feetTop,
                        left: this._leftDimensionsSpacingRenderSize + ((this.store.width / feetCount) * i) - (this.store.feetData.width / 2),
                        side: feetCount / 2 > i ? Side.Left : Side.Right,
                    });
                }
                // render right
                this._renderHairpin({
                    top: feetTop,
                    left: this._leftDimensionsSpacingRenderSize + this.store.width,
                    side: Side.Right,
                });
            }
            else {
                this._render.rectangle({
                    top: feetTop,
                    left: this._leftDimensionsSpacingRenderSize,
                    height: this.store.feetData.height,
                    width: this.store.feetData.width
                });
                // between rolls
                for (let i = 1; i < feetCount; i++) {
                    this._render.rectangle({
                        top: feetTop,
                        left: this._leftDimensionsSpacingRenderSize + ((this.store.width / feetCount) * i) - (this.store.feetData.width / 2),
                        height: this.store.feetData.height,
                        width: this.store.feetData.width
                    });
                }
                this._render.rectangle({
                    top: feetTop,
                    left: (this.store.width - this.store.feetData.width) + this._leftDimensionsSpacingRenderSize,
                    height: this.store.feetData.height,
                    width: this.store.feetData.width
                });
            }
        }
        if (this.isModelView && this.store.hasContinuousDesks && this.store.hasSocket && !this.store.hasFeet && this.showLabels) {
            this._render.text({
                top: this.store.height + this._fontSize / this.sizer,
                left: this.summary([
                    this._leftDimensionsSpacingRenderSize,
                    this.store.width / 2,
                ]),
                isVertical: false,
                fontSize: this._fontSize,
                text: `S1`
            });
        }
    }
    // render vertical desks
    _renderVerticalDesks() {
        let columnNumber = 1;
        const sinus = this.sinus;
        this.store.verticalDesks.forEach((desk, index) => {
            // bevel
            if (index > 0 && desk.underBevel && !desk.last && !(this.store.isLeftBevel && index === this.store.bevelColumnCount)) {
                const offset = this.store.isLeftBevel ? desk.leftOffset : (this.store.width - (desk.leftOffset + desk.width));
                const offset2 = this.store.isLeftBevel ? desk.leftOffset + desk.width : (this.store.width - (desk.leftOffset));
                const height = Math.sqrt(Math.pow(2 / sinus, 2));
                const h = Math.sqrt(Math.pow(offset / sinus, 2) - Math.pow(offset, 2)) + this.store.bevelHeight;
                const h2 = Math.sqrt(Math.pow(offset2 / sinus, 2) - Math.pow(offset2, 2)) + this.store.bevelHeight;
                const x1 = desk.leftOffset + (this.store.isLeftBevel ? desk.width : 0);
                const x2 = desk.leftOffset + (!this.store.isLeftBevel ? desk.width : 0);
                // vertical desks under bevel
                this._render.polygon({
                    top: this.store.height - h2 + height,
                    left: desk.leftOffset + this._leftDimensionsSpacingRenderSize,
                    points: [
                        { x: x1, y: h2 - height },
                        { x: x1, y: 0 },
                        { x: x2, y: h2 - h },
                        { x: x2, y: h2 - height },
                    ]
                });
            }
            else if (!(this.store.isLeftBevel && index === 0) && !(this.store.isBevel && !this.store.isLeftBevel && desk.last)) {
                let height = (this.store.isBevel && !this.store.isLeftBevel && desk.last)
                    ? this.store.bevelHeight
                    : desk.height - (this.store.hasFeet ? this.store.socketData.height : 0);
                let currentDeskTop = this.store.height - (this.store.isBevel && !this.store.isLeftBevel && desk.last ? this.store.bevelHeight : desk.height);
                const currentDeskLeft = desk.leftOffset + this._leftDimensionsSpacingRenderSize;
                if (this.store.hasSlidingDoors && (desk.last || index === 0)) {
                    currentDeskTop -= 6;
                    height += 6;
                }
                this._render.rectangle({
                    top: currentDeskTop,
                    left: currentDeskLeft,
                    width: desk.width,
                    height: height
                });
                // render vertical desk split
                if (typeof desk.split === "number") {
                    this.render.line({
                        topY: (currentDeskTop + (desk.height - desk.split)),
                        bottomY: (currentDeskTop + (desk.height - desk.split)),
                        topX: currentDeskLeft,
                        bottomX: currentDeskLeft + desk.width,
                    });
                }
            }
            // render marks for vertical desks
            if (this.isModelView && this.showLabels) {
                this._render.text({
                    top: this.store.height + (this.store.hasSocket && !this.store.hasFeet ? 2 * (this._fontSize / this.sizer) : this._fontSize / this.sizer),
                    left: desk.leftOffset,
                    text: `W${columnNumber}`,
                    isVertical: false,
                    fontSize: this._fontSize,
                });
                columnNumber = columnNumber + 1;
            }
        });
    }
    // render inner numbers
    _renderInnerNumbers() {
        const minCubbySize = this.store.minCubbySize;
        this.store.horizontalDesks.forEach((desk, index) => {
            const leftOffset = (desk.leftOffset + desk.width / 2);
            // bevel
            if (!this.isInnerView) {
                if ((!desk.underBevel || !desk.last) && (!this.isModelView && !this.isDoorView)) {
                    this._renderDimension({
                        vertical: true,
                        top: desk.topOffset - this.store.socketData.height + desk.thickness,
                        left: leftOffset + this._leftDimensionsSpacingRenderSize,
                        size: desk.structure.height,
                        text: desk.structure.height,
                        textCentered: true,
                        textRotate: false,
                        background: true,
                        simple: true
                    });
                }
            }
            let min = this.sizer * minCubbySize > this.sizer * 6 ? 6 : minCubbySize;
            min = (min * this._render.sizer) < 16 ? 16 / this._render.sizer : min;
            min = (min * this._render.sizer) > 18 ? 18 / this._render.sizer : min;
            // inner numbers
            const top = !this.store.isBevel
                ? desk.topOffset - this.store.socketData.height + desk.thickness
                : desk.topOffset + desk.structure.height - this.store.socketData.height + desk.thickness - min;
            let bevelLeftOffset = 0;
            if (this.store.isBevel && this.store.isLeftBevel) {
                bevelLeftOffset = desk.width - min;
            }
            // inner number around
            this._render.rectangle({
                top: top,
                left: desk.leftOffset + bevelLeftOffset + this._leftDimensionsSpacingRenderSize,
                height: min,
                width: min,
            }, false);
            // inner number text
            if (this.showLabels) {
                this._render.text({
                    top: top + ((!this.isModelView && !this.isDoorView) ? min / 2 : 0),
                    left: (desk.leftOffset + (min / 2) + bevelLeftOffset) + this._leftDimensionsSpacingRenderSize,
                    text: desk.cubbyNumber,
                    isVertical: false,
                    letterSpacing: this.store.width > 200 ? -100 : 0,
                    fontSize: (min * this.sizer < this._fontSize ? this._fontSize : min * this.sizer) * 0.8,
                    options: {
                        textCentered: true,
                    }
                });
            }
        });
    }
    // render bottom horizontal desk
    _renderBottomDesk() {
        this._render.rectangle(Object.assign({}, this.store.bottomDeskData, { top: this.store.bottomDeskData.top, left: this.store.bottomDeskData.left + this._leftDimensionsSpacingRenderSize }));
        // render splits for bottom desk
        if (this.store.hasHorizontalSplits) {
            if (this.store.horizontalBottomSplits.length > 0) {
                this.store.horizontalBottomSplits.forEach((split) => {
                    const splitLine = {
                        topY: this.store.bottomDeskData.top,
                        bottomY: this.store.bottomDeskData.top + this.store.aroundThickness,
                        topX: this._leftDimensionsSpacingRenderSize + split + (this.store.hasFeet || (!this.store.hasFeet && !this.store.hasSocket) ? 0 : this.store.aroundThickness),
                        bottomX: this._leftDimensionsSpacingRenderSize + split + (this.store.hasFeet || (!this.store.hasFeet && !this.store.hasSocket) ? 0 : this.store.aroundThickness),
                    };
                    this._render.line(splitLine);
                });
            }
        }
    }
    // render horizontal desks
    _renderRegales() {
        let lastDesks = [];
        let deskNumber = 0;
        let bottomDeskNumber = 1;
        let currentColumnIndex = 0;
        // render inner regales
        this.store.horizontalDesks.forEach((desk) => {
            // add numbers for model view
            if (this.isModelView) {
                const left = desk.leftOffset + (desk.width / 2) + this._leftDimensionsSpacingRenderSize;
                // render socket marks
                if (this.store.hasSocket && !this.store.hasFeet) {
                    if ((currentColumnIndex !== desk.columnIndex || currentColumnIndex === 0) && !this.store.hasContinuousDesks && this.showLabels) {
                        this._render.text({
                            top: this.store.height + this._fontSize / this.sizer,
                            left: left,
                            isVertical: false,
                            fontSize: this._fontSize,
                            text: `S${desk.columnIndex}`
                        });
                    }
                }
                if (currentColumnIndex !== desk.columnIndex) {
                    deskNumber = 0;
                }
                // add bottom desks numbers if is first horizontal desk
                if (deskNumber === 0) {
                    if ((!this.store.hasContinuousDesks || bottomDeskNumber === 1) && this.showLabels) {
                        this._render.text({
                            top: (this.store.height - this.store.socketData.height) - this.store.aroundThickness,
                            left: left,
                            text: `B.${bottomDeskNumber}`,
                            isVertical: false,
                            fontSize: this._fontSize,
                            options: {
                                decrementOwnHeight: true
                            }
                        });
                        bottomDeskNumber = bottomDeskNumber + 1;
                    }
                }
                deskNumber = deskNumber + 1;
                if (!desk.last && this.showLabels) {
                    this._render.text({
                        top: desk.topOffset - this.store.socketData.height,
                        left: left,
                        text: `${desk.columnIndex}.${deskNumber}`,
                        isVertical: false,
                        fontSize: this._fontSize,
                        options: {
                            decrementOwnHeight: true,
                        }
                    });
                }
                currentColumnIndex = desk.columnIndex;
            }
            if (!desk.underBevel || desk.underBevel && !desk.last) {
                if (!this.store.hasContinuousDesks || !desk.last) {
                    this._render.rectangle({
                        top: desk.topOffset - this.store.socketData.height,
                        left: desk.leftOffset + this._leftDimensionsSpacingRenderSize,
                        width: desk.width,
                        height: desk.height
                    });
                }
                // add desk
                if (desk.last) {
                    lastDesks.push(desk);
                }
            }
        });
        // create continues desks to side view
        if (this.store.hasContinuousDesks) {
            if (this.store.hasColumnsSameHeight) {
                this._render.rectangle({
                    top: this.store.hasSlidingDoors ? SLIDING_DOOR_TOP_COLUMN_OFFSET : 0,
                    left: this._leftDimensionsSpacingRenderSize + (this.store.hasSlidingDoors ? this.store.aroundThickness : 0),
                    width: this.store.width - (this.store.hasSlidingDoors ? 2 * this.store.aroundThickness : 0),
                    height: this.store.aroundThickness
                });
                // render top horizontal splits
                if (this.store.hasHorizontalSplits) {
                    if (typeof this.store.horizontalTopSplits[0] !== "undefined" && this.store.horizontalTopSplits[0].length > 0) {
                        const splits = this.store.horizontalTopSplits[0].filter((item) => typeof item === "number");
                        if (splits) {
                            splits.forEach((split) => {
                                this.render.line({
                                    topY: 0,
                                    bottomY: this.store.aroundThickness,
                                    topX: (split + this._leftDimensionsSpacingRenderSize),
                                    bottomX: (split + this._leftDimensionsSpacingRenderSize),
                                });
                            });
                        }
                    }
                }
                if (this.isModelView && this.showLabels) {
                    this._render.text({
                        top: -1,
                        left: this.store.width / 2 + this._leftDimensionsSpacingRenderSize,
                        text: `D1`,
                        isVertical: false,
                        fontSize: this._fontSize,
                        options: {
                            decrementOwnHeight: true,
                        }
                    });
                }
            }
            else {
                this._renderTopContinuesDesks(lastDesks);
                if (this.isModelView) {
                    this._renderModelViewTopMarks(lastDesks);
                }
            }
        }
        else if (this.isModelView) {
            this._renderModelViewTopMarks(lastDesks);
        }
    }
    _renderModelViewTopMarks(lastDesks) {
        let topDeskNumber = 1;
        lastDesks.forEach((desk, index) => {
            if (this.store.isLeftBevel && index === 0 && this.showLabels) {
                this._render.text({
                    top: desk.topOffset - this.store.socketData.height,
                    left: this.store.bevelData.width / 2 + this._leftDimensionsSpacingRenderSize,
                    text: `D${topDeskNumber}`,
                    isVertical: false,
                    fontSize: this._fontSize,
                    options: {
                        decrementOwnHeight: true,
                    }
                });
                topDeskNumber = topDeskNumber + 1;
            }
            if (!desk.underBevel && this.showLabels) {
                this._render.text({
                    top: desk.topOffset - this.store.socketData.height,
                    left: desk.leftOffset + (desk.width / 2) + this._leftDimensionsSpacingRenderSize,
                    text: `D${topDeskNumber}`,
                    isVertical: false,
                    fontSize: this._fontSize,
                    options: {
                        decrementOwnHeight: true
                    }
                });
                topDeskNumber = topDeskNumber + 1;
            }
        });
        if (this.store.isBevel && !this.store.isLeftBevel && this.showLabels) {
            this._render.text({
                top: 0,
                left: this.store.width - (this.store.bevelData.width / 2),
                text: `D${topDeskNumber}`,
                isVertical: false,
                fontSize: this._fontSize,
                options: {
                    decrementOwnHeight: true,
                }
            });
        }
    }
    _renderTopContinuesDesks(desks) {
        let totalWidth = 0;
        let prevTopOffset = 0;
        const groups = this._groupDesksByColumnHeight(desks);
        for (let i = 0; i < groups.length; i++) {
            let width = (i === 0) ? this.store.aroundThickness : 0;
            let groupOffset = 0;
            const lastGroup = i === groups.length - 1;
            const currentTopOffset = groups[i].topOffset;
            const nextIsHigher = groups[i + 1] !== undefined && groups[i + 1].topOffset < currentTopOffset;
            const prevIsHigher = prevTopOffset < currentTopOffset;
            if (i === 0) {
                groupOffset = this.store.aroundThickness * -1;
            }
            groups[i].desks.forEach((desk, deskIndex) => {
                const lastDesk = (groups[i].desks.length - 1) === deskIndex;
                width = width + desk.width;
                if (!lastDesk) {
                    width = width + this.store.thickness.vertical;
                }
                else if (!nextIsHigher) {
                    width = width + this.store.thickness.vertical;
                }
                if (lastDesk) {
                    if (!prevIsHigher && !nextIsHigher && i > 0 && !lastGroup || !prevIsHigher && nextIsHigher && i > 0 && !lastGroup) {
                        groupOffset = this.store.thickness.vertical * -1;
                        width = width + this.store.thickness.vertical;
                    }
                    else if (i === 0 && nextIsHigher) {
                        groupOffset = this.store.aroundThickness * -1;
                    }
                    if (lastGroup) {
                        width = width + this.store.aroundThickness;
                        if (prevIsHigher) {
                            width = width - this.store.thickness.vertical;
                        }
                        else {
                            groupOffset = this.store.thickness.vertical * -1;
                        }
                    }
                }
            });
            const left = groups[i].desks[0].leftOffset + groupOffset + this._leftDimensionsSpacingRenderSize;
            this._render.rectangle({
                top: currentTopOffset - this.store.socketData.height,
                left: left,
                height: this.store.aroundThickness,
                width: width
            });
            // render top horizontal splits
            if (this.store.hasHorizontalSplits) {
                if (typeof this.store.horizontalTopSplits[i] !== "undefined" && this.store.horizontalTopSplits[i].length > 0) {
                    const splits = this.store.horizontalTopSplits[i].filter((item) => typeof item === "number");
                    if (splits) {
                        splits.forEach((split) => {
                            this.render.line({
                                topY: currentTopOffset - this.store.socketData.height,
                                bottomY: currentTopOffset - this.store.socketData.height + this.store.aroundThickness,
                                topX: groups[i].desks[0].leftOffset + split + this._leftDimensionsSpacingRenderSize + groupOffset,
                                bottomX: groups[i].desks[0].leftOffset + split + this._leftDimensionsSpacingRenderSize + groupOffset,
                            });
                        });
                    }
                }
            }
            // continues top desk
            if (!this.isModelView && !this.isDoorView && !this.isInnerView) {
                this._renderDimension({
                    top: this.store.height + 2 * this._renderDimensionSpacing,
                    left: left,
                    size: width,
                    vertical: false,
                });
            }
            totalWidth = totalWidth + width;
            prevTopOffset = groups[i].topOffset;
        }
        if (this.store.isBevel && (!this.isModelView && !this.isDoorView && !this.isInnerView)) {
            this._renderDimension({
                top: this.store.height + (2 * this._renderDimensionSpacing),
                left: (!this.store.isLeftBevel ? totalWidth : 0) + this._leftDimensionsSpacingRenderSize,
                size: this.store.width - totalWidth,
                vertical: false
            });
        }
    }
    _groupDesksByColumnHeight(desks) {
        let groups = [];
        desks.forEach((desk, index) => {
            const i = groups.length > 0 ? groups.length - 1 : 0;
            if (groups.length === 0) {
                groups.push({
                    desks: [desk],
                    topOffset: desk.topOffset,
                    groupWidth: desk.width + this.store.getVerticalThickness(index),
                });
            }
            else if (groups[i].topOffset !== desk.topOffset) {
                groups.push({
                    desks: [desk],
                    topOffset: desk.topOffset,
                    groupWidth: desk.width + this.store.getVerticalThickness(index),
                });
            }
            else {
                groups[i].desks.push(desk);
                groups[i].groupWidth = groups[i].groupWidth + desk.width + this.store.getVerticalThickness(index);
            }
        });
        if ((this.isModelView && this.isDoorView && this.isInnerView) && this.store.hasContinuousDesks) {
            this._renderModelViewTopMarks([...groups].map((g) => {
                return Object.assign({}, g.desks[0], { left: g.desks[0].leftOffset, width: g.groupWidth });
            }));
        }
        return groups;
    }
    // render left Dimensions
    _renderLeftDimensions() {
        this.store.leftDimensions.forEach(({ height, isBevel }, index) => {
            const left = (index * this._renderDimensionSpacing) + this._renderDimensionSpacing;
            this._renderDimension({
                top: (this.store.height - height),
                left: left,
                size: height,
                vertical: true,
                text: index === 0 ? `${height} cm` : undefined,
            });
            // render bevel dimension
            if (isBevel && (this.store.height - height) > 0) {
                this._renderDimension({
                    top: 0,
                    left: left,
                    size: this.store.height - height,
                    vertical: true,
                });
            }
        });
        if (this.store.coverstrip) {
            this._renderDimension({
                top: this.store.height - this.store.coverstrip.longVerticalDeskSize,
                left: 0,
                size: this.store.coverstrip.longVerticalDeskSize,
                vertical: true,
                text: `${this.store.coverstrip.longVerticalDeskSize.toFixed(1).replace(/[.,]0$/, "")} cm`
            });
        }
    }
    // render bottom dimensions
    _renderBottomDimensions() {
        let left = this._leftDimensionsSpacingRenderSize;
        let top = this.store.height + this._renderDimensionSpacing;
        // columns dimensions
        this.store.verticalDesks.forEach((desk, index) => {
            if (index > 0) {
                // columns
                if (index === 1) {
                    left = left + this.store.getVerticalThickness(index);
                }
                this._renderDimension({
                    top: top,
                    left: left + (this._leftDimensionsSpacingRenderSize === left ? this.store.aroundThickness : 0),
                    size: desk.structure.width,
                    vertical: false,
                    textCentered: true,
                });
                left = left + desk.structure.width + desk.thickness;
            }
        });
        let totalDimensionTop = this.summary([top, this._renderDimensionSpacing, !this.store.hasColumnsSameHeight && this.store.hasContinuousDesks ? this._renderDimensionSpacing : 0]);
        // total width dimension
        this._renderDimension({
            top: totalDimensionTop,
            left: this._leftDimensionsSpacingRenderSize,
            size: this.store.width,
            text: `${this.store.width} cm`,
            vertical: false,
            textCentered: true
        });
        // Render bottom splits dimensions
        if (this._store.hasHorizontalBottomSplits) {
            totalDimensionTop += this._renderDimensionSpacing;
            if (this.store.horizontalBottomSplits.length > 0) {
                let left = 0;
                this.store.horizontalBottomSplits.forEach((split) => {
                    const dimension = {
                        vertical: false,
                        top: totalDimensionTop,
                        left: left + this._leftDimensionsSpacingRenderSize,
                        size: split - left,
                    };
                    left = split;
                    this._renderDimension(dimension);
                });
                const dimension = {
                    vertical: false,
                    top: totalDimensionTop,
                    left: left + this._leftDimensionsSpacingRenderSize,
                    size: this._store.width - left,
                };
                this._renderDimension(dimension);
            }
        }
        if (this.store.hasCoverStrip) {
            totalDimensionTop += this._renderDimensionSpacing;
            const coverStripSize = this.store.width + (2 * this.store.coverstrip.thickness);
            this._renderDimension({
                vertical: false,
                top: totalDimensionTop,
                left: this._leftDimensionsSpacingRenderSize + (this.store.coverstrip.thickness * -1),
                size: coverStripSize,
                text: `${coverStripSize} cm`
            });
        }
    }
    // render bevel
    _renderBevel() {
        if (!this.store.isBevel) {
            return;
        }
        const sinus = Math.sin(((90 + this.bevelAngle) / 2) * (Math.PI / 180));
        const h = Math.sqrt(Math.pow((2 / sinus), 2) - 4);
        let left = this._leftDimensionsSpacingRenderSize;
        if (!this.store.isLeftBevel) {
            left += (this.store.width - this.store.aroundThickness) - this.store.bevelData.width;
        }
        const posY = this.store.height - (this.store.bevelHeight);
        let points = [
            { x: 0, y: 0 },
            { x: (this.store.bevelData.width * -1), y: posY },
            { x: ((this.store.bevelData.width) * -1) + this.store.aroundThickness, y: posY + h },
            { x: this.store.aroundThickness, y: h },
            { x: 0, y: 0 }
        ];
        this._render.polygon({
            top: 0,
            left: left,
            points: points,
        }, true, 1, !this.store.isLeftBevel);
        left = this._leftDimensionsSpacingRenderSize;
        if (!this.store.isLeftBevel) {
            left += this.store.width - this.store.aroundThickness;
        }
        let height = this.store.height - this.store.socket.height;
        let positionX1 = this.store.width + this._leftDimensionsSpacingRenderSize;
        let positionX2 = positionX1 - this.store.aroundThickness;
        this._render.polygon({
            top: this.store.height - this.store.bevelHeight,
            left: left,
            points: [
                { x: positionX2, y: height },
                { x: positionX1, y: height },
                { x: positionX1, y: height - this.store.bevelHeight },
                { x: positionX2, y: (height - this.store.bevelHeight) + h },
                { x: positionX2, y: height },
            ],
        }, true, 1, this.store.isLeftBevel);
    }
    /**
     * Render bevel for cover strip
     * @private
     * @return void
     */
    _renderBevelCoverStrip() {
        if (!this.store.hasCoverStrip) {
            return;
        }
        const coverStrip = this.store.coverstrip;
        // longer vertical desk if bevel, otherwise both desks
        let sideCoverStripRectangle = {
            top: this.store.height - this.store.coverstrip.longVerticalDeskSize,
            left: this._leftDimensionsSpacingRenderSize + this.store.width,
            height: this.store.coverstrip.longVerticalDeskSize,
            width: coverStrip.thickness,
        };
        if (!this.store.isBevel || this.store.isLeftBevel) {
            //right desk
            this.render.rectangle(sideCoverStripRectangle);
        }
        if (!this.store.isBevel || !this.store.isLeftBevel) {
            sideCoverStripRectangle.left -= this.store.width + coverStrip.thickness;
            this.render.rectangle(sideCoverStripRectangle);
        }
        const sinus2 = Math.sin(((90 + this.bevelAngle) / 2) * (Math.PI / 180));
        const h = Math.sqrt(Math.pow((2 / sinus2), 2) - 4);
        // first left bevel desk
        if (this.store.isBevel) {
            const height = (this.store.height - this.store.socket.height);
            const rightOffset = coverStrip.thickness + this._leftDimensionsSpacingRenderSize;
            const leftOffset = this._leftDimensionsSpacingRenderSize;
            // shorter vertical desk
            let points = [
                { x: rightOffset, y: height + h },
                { x: leftOffset, y: height + h },
                { x: leftOffset, y: height - this.store.bevelHeight },
                { x: rightOffset, y: (height - this.store.bevelHeight) + h },
                { x: rightOffset, y: height + h },
            ];
            let top = this.store.height - (this.store.bevelHeight + h);
            let left = this._leftDimensionsSpacingRenderSize - coverStrip.thickness;
            if (!this.store.isLeftBevel) {
                left = this._leftDimensionsSpacingRenderSize + this.store.width;
            }
            this._render.polygon({
                top: top,
                left: left,
                points: points,
            }, true, 1, !this.store.isLeftBevel);
            // top desk
            let topDeskLeft = this._leftDimensionsSpacingRenderSize - coverStrip.thickness;
            if (this.store.isLeftBevel) {
                topDeskLeft += this.store.bevelData.width;
                topDeskLeft += (2 * h);
            }
            if (this.store.flatColumnCount !== 0) {
                this._render.rectangle({
                    top: coverStrip.thickness * -1,
                    left: 2 * h > coverStrip.thickness && this.store.isLeftBevel ? topDeskLeft - h : topDeskLeft,
                    width: (this.store.width - this.store.bevelData.width + (2 * coverStrip.thickness) - (2 * h)) + (2 * h > coverStrip.thickness ? h : 0),
                    height: coverStrip.thickness
                });
            }
            // bevel desk
            if (!this.store.isLeftBevel) {
                left -= this.store.bevelData.width;
                if (this.store.flatColumnCount === 0) {
                    left -= this.store.thickness.around;
                }
            }
            if (this.store.flatColumnCount !== 0) {
                const posY = this.store.height - this.store.bevelHeight + coverStrip.thickness;
                points = [
                    { x: coverStrip.thickness, y: coverStrip.thickness },
                    { x: ((this.store.bevelData.width) * -1) + coverStrip.thickness, y: posY },
                    { x: (this.store.bevelData.width * -1), y: posY - h },
                    { x: 2 * h > coverStrip.thickness ? h : (2 * h), y: 0 },
                    { x: coverStrip.thickness, y: coverStrip.thickness }
                ];
                top = this.store.height - this.store.coverstrip.longVerticalDeskSize;
            }
            else {
                const posY = this.store.coverstrip.longVerticalDeskSize - this.store.bevelHeight;
                const posX = coverStrip.thickness + this.store.bevelData.width + this.store.thickness.around;
                points = [
                    { x: coverStrip.thickness, y: posY },
                    { x: 0, y: posY - h },
                    { x: posX, y: 0 },
                    { x: posX, y: this.store.coverstrip.longVerticalDeskSize - this.store.height - Math.tan(this.store.bevelData.angle) * this.store.thickness.around },
                    { x: coverStrip.thickness, y: posY },
                ];
                top = this.store.height - this.store.coverstrip.longVerticalDeskSize;
            }
            this._render.polygon({
                top,
                left,
                points,
            }, true, 1, !this.store.isLeftBevel);
        }
        // Top non bevel desk
        if (!this.store.isBevel) {
            this._render.rectangle({
                top: (coverStrip.thickness * -1),
                left: this._leftDimensionsSpacingRenderSize + (coverStrip.thickness * -1),
                width: this.store.width + (2 * coverStrip.thickness),
                height: coverStrip.thickness,
            });
        }
    }
    get sideViewLeftOffset() {
        return this.summary([
            this.store.width,
            this._leftDimensionsSpacingRenderSize,
            this._sideViewDimensionSpacingRenderSize,
            this.store.hasCoverStrip ? this.store.coverstrip.thickness : 0,
        ]);
    }
    // render side view
    _renderSideView() {
        const sideViewLeftOffset = this.sideViewLeftOffset;
        // render by skirting
        if (!this.store.hasSkirtingBoard) {
            if (!this.store.hasFeet) {
                // without feet
                if (this.store.hasSlidingDoors && this.store.slidingDoors.type === SlidingDoorsType.DOUBLE_RIGHT) {
                    this._render.rectangle({
                        top: 0,
                        left: sideViewLeftOffset,
                        width: this.store.depth + SLIDING_DOOR_DEPTH_OFFSET + SLIDING_DOOR_DESK_THICKNESS,
                        height: this.store.height,
                    });
                }
                this._render.rectangle({
                    top: 0,
                    left: sideViewLeftOffset,
                    width: this.store.depth,
                    height: this.store.height,
                });
            }
            else {
                // side view with feet
                const heightWithoutFeet = this.store.height - this.store.feetData.height;
                this._render.rectangle({
                    top: 0,
                    left: sideViewLeftOffset,
                    width: this.store.depth,
                    height: heightWithoutFeet,
                });
                // render feet for side view
                if (this.store.hasFeetLongDesk) {
                    this._render.rectangle({
                        top: heightWithoutFeet,
                        left: sideViewLeftOffset + ((this.store.depth - this.store.feetData.depth) / 2),
                        width: this.store.feetData.depth,
                        height: this.store.feetData.height,
                    });
                }
                else if (this.store.hasFeetRolls) {
                    // left roll
                    this._renderRoll({
                        top: heightWithoutFeet,
                        left: sideViewLeftOffset,
                        width: this.store.feetData.width,
                        height: this.store.feetData.height,
                        sideView: true
                    });
                    if (this.store.feetData.hasFeetInCenter) {
                        this._renderRoll({
                            top: heightWithoutFeet,
                            left: sideViewLeftOffset + (this.store.depth - this.store.feetData.depth) / 2,
                            width: this.store.feetData.width,
                            height: this.store.feetData.height,
                            sideView: true
                        });
                    }
                    // right roll
                    this._renderRoll({
                        top: heightWithoutFeet,
                        left: sideViewLeftOffset + this.store.depth - this.store.feetData.depth,
                        width: this.store.feetData.width,
                        height: this.store.feetData.height,
                        sideView: true
                    });
                }
                else if (this.store.hasFeetMetal5) {
                    this._renderMetalFeet5(this.store.height - this.store.feetData.height, sideViewLeftOffset, this.store.feetData.width, this.store.feetData.height, true);
                    if (this.store.feetData.hasFeetInCenter) {
                        this._renderMetalFeet5(this.store.height - this.store.feetData.height, sideViewLeftOffset + (this.store.depth - this.store.feetData.height) / 2, this.store.feetData.width, this.store.feetData.height, false);
                    }
                    this._renderMetalFeet5(this.store.height - this.store.feetData.height, sideViewLeftOffset + (this.store.depth - this.store.feetData.height), this.store.feetData.width, this.store.feetData.height, false);
                }
                else if (this.store.hasFeetWoodFrame) {
                    let depthOffset = 2;
                    // left
                    this._renderWoodFrameFeet(heightWithoutFeet, sideViewLeftOffset + depthOffset, true);
                    // middle
                    this._render.rectangle({
                        top: heightWithoutFeet,
                        left: sideViewLeftOffset + this.store.feetData.width + depthOffset,
                        height: this.store.feetData.width,
                        width: this.store.depth - ((this.store.feetData.width + depthOffset) * 2),
                    });
                    // right
                    this._renderWoodFrameFeet(heightWithoutFeet, sideViewLeftOffset + this.store.depth - this.store.feetData.width - depthOffset, false);
                }
                else if (this.store.hasFeetMetalFrame1) {
                    // left
                    this._render.rectangle({
                        top: heightWithoutFeet + this.store.feetData.width,
                        left: sideViewLeftOffset,
                        height: this.store.socket.height - this.store.feetData.width,
                        width: this.store.feetData.width,
                    });
                    // middle
                    this._render.rectangle({
                        top: heightWithoutFeet,
                        left: sideViewLeftOffset,
                        height: this.store.feetData.width,
                        width: this.store.depth,
                    });
                    // right
                    this._render.rectangle({
                        top: heightWithoutFeet + this.store.feetData.width,
                        left: sideViewLeftOffset + this.store.depth - this.store.feetData.width,
                        height: this.store.socket.height - this.store.feetData.width,
                        width: this.store.feetData.width,
                    });
                    // strut
                    if (this.store.width > METAL_FRAME_1_STRUT_LIMIT) {
                        this._render.rectangle({
                            top: heightWithoutFeet + this.store.feetData.width,
                            left: sideViewLeftOffset + (this.store.depth - this.store.feetData.width) / 2,
                            height: this.store.socket.height - this.store.feetData.width,
                            width: this.store.feetData.width,
                        });
                    }
                }
                else if (this.store.hasFeetMetalFrame2) {
                    // left
                    this._render.rectangle({
                        top: heightWithoutFeet + this.store.feetData.width,
                        left: sideViewLeftOffset,
                        height: this.store.socket.height - (this.store.feetData.width * 2),
                        width: this.store.feetData.width,
                    });
                    // middle top
                    this._render.rectangle({
                        top: heightWithoutFeet,
                        left: sideViewLeftOffset,
                        height: this.store.feetData.width,
                        width: this.store.depth,
                    });
                    // middle bottom
                    this._render.rectangle({
                        top: heightWithoutFeet + this.store.socket.height - this.store.feetData.width,
                        left: sideViewLeftOffset,
                        height: this.store.feetData.width,
                        width: this.store.depth,
                    });
                    // right
                    this._render.rectangle({
                        top: heightWithoutFeet + this.store.feetData.width,
                        left: sideViewLeftOffset + this.store.depth - this.store.feetData.width,
                        height: this.store.socket.height - (this.store.feetData.width * 2),
                        width: this.store.feetData.width,
                    });
                    // strut
                    if (this.store.width > METAL_FRAME_2_STRUT_LIMIT) {
                        this._render.rectangle({
                            top: heightWithoutFeet + this.store.feetData.width,
                            left: sideViewLeftOffset + (this.store.depth - this.store.feetData.width) / 2,
                            height: this.store.socket.height - (this.store.feetData.width * 2),
                            width: this.store.feetData.width,
                        });
                    }
                }
                else if (this.store.hasFeetHairpin) {
                    // Left
                    this._renderHairpin({
                        top: heightWithoutFeet,
                        left: sideViewLeftOffset,
                        side: Side.Left,
                    });
                    if (this.store.feetData.hasFeetInCenter) {
                        this._renderHairpin({
                            top: heightWithoutFeet,
                            left: sideViewLeftOffset + this.store.depth / 2,
                            side: Side.Right,
                        });
                    }
                    // Right
                    this._renderHairpin({
                        top: heightWithoutFeet,
                        left: sideViewLeftOffset + this.store.depth,
                        side: Side.Right,
                    });
                }
                else {
                    this._render.rectangle({
                        top: heightWithoutFeet,
                        left: sideViewLeftOffset,
                        width: this.store.feetData.depth,
                        height: this.store.feetData.height,
                    });
                    if (this.store.feetData.hasFeetInCenter) {
                        this._render.rectangle({
                            top: heightWithoutFeet,
                            left: sideViewLeftOffset + (this.store.depth - this.store.feetData.depth) / 2,
                            width: this.store.feetData.depth,
                            height: this.store.feetData.height,
                        });
                    }
                    this._render.rectangle({
                        top: heightWithoutFeet,
                        left: sideViewLeftOffset + (this.store.depth - this.store.feetData.depth),
                        width: this.store.feetData.depth,
                        height: this.store.feetData.height,
                    });
                }
            }
            // render side view split lines
            if (this.store.firstDeskSplit !== null) {
                const splitLine = {
                    topY: this.store.height - this.store.firstDeskSplit,
                    bottomY: this.store.height - this.store.firstDeskSplit,
                    topX: sideViewLeftOffset,
                    bottomX: sideViewLeftOffset + this.store.depth,
                };
                this._render.line(splitLine);
            }
        }
        else {
            this._render.polygon({
                top: 0,
                left: sideViewLeftOffset,
                points: [
                    { x: 0, y: 0 },
                    { x: this.store.depth, y: 0 },
                    { x: this.store.depth, y: this.store.height },
                    { x: this.store.skirtingBoard.width, y: this.store.height },
                    { x: this.store.skirtingBoard.width, y: this.store.height - this.store.skirtingBoard.height },
                    { x: 0, y: this.store.height - this.store.skirtingBoard.height },
                    { x: 0, y: 0 },
                ]
            });
        }
        if (this.store.hasContinuousDesks && !this.store.hasSlidingDoors) {
            // render top desk
            this._render.rectangle({
                top: 0,
                left: sideViewLeftOffset,
                width: this.store.depth + this.store.topDeskOverhang,
                height: this.store.aroundThickness,
            });
            // render bottom desk
            if (!this.store.hasSocket && !this.store.hasFeet) {
                if (!this.store.hasSkirtingBoard) {
                    this._render.rectangle({
                        top: this.store.height - this.store.aroundThickness,
                        left: sideViewLeftOffset,
                        width: this.store.depth,
                        height: this.store.aroundThickness,
                    });
                }
                else {
                    this._render.rectangle({
                        top: this.store.height - this.store.aroundThickness,
                        left: sideViewLeftOffset + this.store.skirtingBoard.width,
                        width: this.store.depth - this.store.skirtingBoard.width,
                        height: this.store.aroundThickness,
                    });
                }
            }
        }
        if (!this.isModelView && !this.isDoorView && !this.isInnerView) {
            let dimensionY = this.store.height;
            // bottom dimension for skirting board
            if (this.store.hasSkirtingBoard) {
                dimensionY += this._renderDimensionSpacing;
                // bottom
                let skirtingBoardTextYOffset = 0;
                if (this.store.skirtingBoard.width <= 4 && this.sizer < 4) {
                    skirtingBoardTextYOffset = ((this._renderDimensionSpacing * 0.25) * -1);
                }
                this._renderDimension({
                    top: this.store.height + this._renderDimensionSpacing,
                    left: sideViewLeftOffset,
                    size: this.store.skirtingBoard.width,
                    vertical: false,
                    textCentered: true,
                    textOffset: skirtingBoardTextYOffset,
                });
                this._renderDimension({
                    top: dimensionY,
                    left: sideViewLeftOffset + this.store.skirtingBoard.width,
                    size: this.store.depth - this.store.skirtingBoard.width,
                    vertical: false,
                    textCentered: true
                });
            }
            // bottom dimension
            dimensionY += this._renderDimensionSpacing;
            const depth = this.store.depth
                + (this.store.hasSlidingDoors
                    && this.store.slidingDoors.type === SlidingDoorsType.DOUBLE_RIGHT
                    ? SLIDING_DOOR_DESK_THICKNESS + SLIDING_DOOR_DEPTH_OFFSET
                    : 0);
            this._renderDimension({
                top: dimensionY,
                left: sideViewLeftOffset,
                size: depth,
                vertical: false,
                text: depth <= 30 ? depth.toFixed(1).replace(/[.,]0$/, "") : `${depth.toFixed(1).replace(/[.,]0$/, "")} cm`,
                textCentered: true
            });
            // sliding doors dimension
            if (this.store.hasSlidingDoors) {
                dimensionY += this._renderDimensionSpacing;
                const depth = this.store.depth + this.store.slidingDoorsDepth;
                this._renderDimension({
                    top: dimensionY,
                    left: sideViewLeftOffset,
                    size: depth,
                    vertical: false,
                    text: depth <= 40 ? depth.toFixed(1).replace(/[.,]0$/, "") : `${depth.toFixed(1).replace(/[.,]0$/, "")} cm`,
                    textCentered: true
                });
            }
            // outer doors or desk dimension
            if (this.store.outerDoorOrDeskOffset > 0) {
                dimensionY += this._renderDimensionSpacing;
                this._renderDimension({
                    top: dimensionY,
                    left: sideViewLeftOffset,
                    size: this.store.depth + this.store.outerDoorOrDeskOffset,
                    vertical: false,
                    text: `${this.store.depth + this.store.outerDoorOrDeskOffset} cm`,
                    textCentered: true
                });
            }
            // left dimension
            this._renderDimension({
                top: 0,
                left: sideViewLeftOffset - (this.store.hasSkirtingBoard
                    ? 2 * this._renderDimensionSpacing
                    : this._renderDimensionSpacing),
                size: this.store.height,
                vertical: true,
                text: `${this.store.height} cm`,
                textOffset: (this.store.socketData.height / 2),
            });
            if (this.store.hasSkirtingBoard) {
                this._renderDimension({
                    top: 0,
                    left: sideViewLeftOffset - this._renderDimensionSpacing,
                    size: this.store.height - this.store.skirtingBoard.height,
                    vertical: true,
                });
                this._renderDimension({
                    top: this.store.height - this.store.skirtingBoard.height,
                    left: sideViewLeftOffset - this._renderDimensionSpacing,
                    size: this.store.skirtingBoard.height,
                    vertical: true,
                });
            }
            if (this.store.hasCoverStrip) {
                let coverStripLeftOffset = sideViewLeftOffset - 2 * this._renderDimensionSpacing;
                if (this.store.hasSkirtingBoard) {
                    coverStripLeftOffset -= this._renderDimensionSpacing;
                }
                this._renderDimension({
                    top: this.store.height - this.store.coverstrip.longVerticalDeskSize,
                    left: coverStripLeftOffset,
                    size: this.store.coverstrip.longVerticalDeskSize,
                    vertical: true,
                    text: `${this.store.coverstrip.longVerticalDeskSize.toFixed(1).replace(/[.,]0$/, "")} cm`,
                });
            }
        }
    }
    /**
     * Render outer doors in side view
     * @private
     * @return void
     */
    _renderSideViewOuterDoors() {
        if (this.store.outerDoors.length === 0) {
            return;
        }
        // outer doors
        const doorOverflow = this.doorOverflow;
        for (let door of this.store.outerDoors.reverse()) {
            const y = this.getDoorPositionY(door);
            let doorRectangle = {
                top: y,
                left: this.sideViewLeftOffset + this.store.depth,
                width: door.thickness,
                height: door.height,
            };
            if (door.type === DoorType.Sliding) {
                doorRectangle.top -= SLIDING_DOOR_TOP_COLUMN_OFFSET + this.store.aroundThickness;
                doorRectangle.height += SLIDING_DOOR_TOP_COLUMN_OFFSET + 2 * this.store.aroundThickness + SLIDING_DOOR_BOTTOM_OFFSET;
                doorRectangle.left += door.zOffset;
                if (door.zOffset < 0) {
                    continue;
                }
            }
            else {
                if (!this.store.isWardrobe) {
                    doorRectangle.top -= doorOverflow;
                    doorRectangle.height += (2 * doorOverflow);
                }
                else {
                    let heightIncrement = 0;
                    if (this.hasBeforeDoors(door)) {
                        doorRectangle.top -= doorOverflow;
                        heightIncrement += doorOverflow;
                    }
                    else {
                        doorRectangle.top -= this.store.aroundThickness;
                        heightIncrement += this.store.aroundThickness;
                    }
                    if (this.hasAfterDoors(door)) {
                        heightIncrement += doorOverflow;
                    }
                    else {
                        heightIncrement += this.store.aroundThickness;
                    }
                    doorRectangle.height += heightIncrement;
                }
            }
            this._render.rectangle(doorRectangle, false, 1, false, OUTER_DOOR_BACKGROUND);
        }
        // first left outer door under left bevel line
        if (this.store.isLeftBevel && this.store.outerDoors[0].underBevel) {
            let y = (this.store.height - this.store.bevelHeight) - (Math.sqrt(Math.pow(0 / this.sinus, 2) - Math.pow(0, 2)));
            const door = this.store.outerDoors[0];
            this._render.line({
                topY: y,
                bottomY: y,
                bottomX: this.sideViewLeftOffset + this.store.depth,
                topX: this.sideViewLeftOffset + this.store.depth + door.thickness
            });
        }
    }
    _renderSideViewCoverStrip() {
        const coverStrip = this.store.coverstrip;
        if (coverStrip !== undefined) {
            let leftOffset = this.sideViewLeftOffset;
            leftOffset += this.store.depth;
            leftOffset -= coverStrip.width;
            leftOffset += this.store.outerDoorOrDeskOffset;
            this._render.rectangle({
                top: this.store.height - this.store.coverstrip.longVerticalDeskSize,
                left: leftOffset,
                width: coverStrip.width,
                height: this.store.coverstrip.longVerticalDeskSize,
            }, false, 1, false, OUTER_DOOR_BACKGROUND);
        }
    }
    get doorOverflow() {
        return ((this.store.getHorizontalThickness(false) - 0.3)) / 2;
    }
    doorInSameHorizontalPosition(x) {
        return this.store.outerDoors.filter(d => d.x === x);
    }
    getDoorPositionY(door) {
        return this.store.height - (door.y + door.height + this.store.socketData.height);
    }
    hasBeforeDoors(door) {
        const doors = this.doorInSameHorizontalPosition(door.x);
        return doors.filter((d) => {
            const positionY = this.getDoorPositionY(door) - this.store.aroundThickness;
            return (this.getDoorPositionY(d) + d.height + this.store.getHorizontalThickness(false)) === positionY;
        }).length > 0;
    }
    hasAfterDoors(door) {
        const doors = this.doorInSameHorizontalPosition(door.x);
        return doors.filter((d) => {
            const positionY = this.getDoorPositionY(door) + door.height + this.store.maxHorizontalThickness;
            return this.getDoorPositionY(d) === positionY;
        }).length > 0;
    }
    _renderRoll(roll) {
        const rollSiteWidth = roll.width / 3;
        const rollSiteHeight = roll.height * 0.6;
        const middleFeetWidth = ((roll.width / 3) / 2);
        const middleFeetHeight = (roll.height * 0.4) + ((roll.height * 0.6) / 3);
        // middle roll feet
        this._render.rectangle({
            top: roll.top,
            left: roll.left + rollSiteWidth + (middleFeetWidth / 2),
            width: middleFeetWidth,
            height: (roll.height * 0.4) + (rollSiteHeight / 3)
        });
        if (roll.sideView) {
            // roll sites middle
            this._render.rectangle({
                top: roll.top + middleFeetHeight,
                left: roll.left + rollSiteWidth,
                width: rollSiteWidth,
                height: rollSiteWidth / 3
            });
            // left roll site
            this._render.rectangle({
                top: roll.top + middleFeetHeight - (rollSiteHeight / 3),
                left: roll.left,
                width: rollSiteWidth,
                height: (roll.height * 0.4) + (rollSiteHeight / 3)
            });
            // right roll site
            this._render.rectangle({
                top: roll.top + middleFeetHeight - (rollSiteHeight / 3),
                left: roll.left + 2 * rollSiteWidth,
                width: rollSiteWidth,
                height: (roll.height * 0.4) + (rollSiteHeight / 3)
            });
            // keeping roll desk
            this._render.rectangle({
                top: roll.top,
                left: roll.left,
                width: roll.width,
                height: roll.height * 0.1
            });
        }
        else {
            // keeping roll desk
            this._render.rectangle({
                top: roll.top,
                left: roll.left,
                width: roll.width,
                height: roll.height * 0.1
            });
            this._render.circle({
                top: roll.top + middleFeetHeight - (rollSiteHeight / 3),
                left: roll.left,
                radius: (roll.width / 2)
            });
        }
    }
    _renderHairpin(hairpin) {
        if (hairpin.side === Side.Left) {
            this._render.rectangle({
                top: hairpin.top,
                left: hairpin.left,
                width: 1,
                height: this.store.feetData.height - 2.9,
            });
            this._render.rectangle({
                top: hairpin.top,
                left: hairpin.left + 5,
                width: 1,
                height: this.store.feetData.height - 2.6,
                angle: 8,
            });
            this._render.circle({
                top: hairpin.top + this.store.feetData.height - 5,
                left: hairpin.left,
                radius: 1.79,
                startAngle: 0,
                endAngle: Math.PI,
            }, true);
            this._render.circle({
                top: hairpin.top + this.store.feetData.height - 4.2,
                left: hairpin.left + 1,
                radius: 0.8,
                startAngle: 0,
                endAngle: Math.PI,
            });
        }
        else {
            this._render.rectangle({
                top: hairpin.top,
                left: hairpin.left - 1,
                width: 1,
                height: this.store.feetData.height - 2.9,
            });
            this._render.rectangle({
                top: hairpin.top,
                left: hairpin.left - 6,
                width: 1,
                height: this.store.feetData.height - 2.6,
                angle: -8,
            });
            this._render.circle({
                top: hairpin.top + this.store.feetData.height - 5,
                left: hairpin.left - 3.57,
                radius: 1.79,
                startAngle: 0,
                endAngle: Math.PI,
            }, true);
            this._render.circle({
                top: hairpin.top + this.store.feetData.height - 4.2,
                left: hairpin.left - 2.6,
                radius: 0.8,
                startAngle: 0,
                endAngle: Math.PI,
            });
        }
    }
    _renderMetalFeet5(top, left, width, height, leftSite) {
        if (leftSite) {
            this._render.polygon({
                top: this.store.height - height,
                left: left,
                points: [
                    { x: 0, y: 0 },
                    { x: height, y: 0 },
                    { x: height, y: width },
                    { x: width, y: width },
                    { x: width, y: height },
                    { x: 0, y: height },
                    { x: 0, y: 0 },
                ]
            });
        }
        else {
            this._render.polygon({
                top: this.store.height - height,
                left: left,
                points: [
                    { x: 0, y: 0 },
                    { x: height, y: 0 },
                    { x: height, y: width },
                    { x: height, y: height },
                    { x: height - width, y: height },
                    { x: height - width, y: width },
                    { x: 0, y: width },
                    { x: 0, y: 0 },
                ]
            });
        }
    }
    _renderWoodFrameFeet(feetTop = 0, feetLeft = 0, isLeft = false) {
        if (isLeft) {
            // left
            this._render.polygon({
                top: feetTop,
                left: feetLeft,
                points: [
                    { x: 0, y: feetTop },
                    { x: this.store.feetData.width, y: feetTop },
                    { x: this.store.feetData.width, y: feetTop + 7 },
                    { x: this.store.feetData.width - 1.5, y: feetTop + this.store.socket.height },
                    { x: 0, y: feetTop + this.store.socket.height },
                    { x: 0, y: feetTop },
                ],
            });
        }
        else {
            // right
            this._render.polygon({
                top: feetTop,
                left: feetLeft,
                points: [
                    { x: 0, y: feetTop },
                    { x: this.store.feetData.width, y: feetTop },
                    { x: this.store.feetData.width, y: feetTop + this.store.socket.height },
                    { x: 1.5, y: feetTop + this.store.socket.height },
                    { x: 0, y: feetTop + 7 },
                    { x: 0, y: feetTop },
                ]
            });
        }
    }
    // render dimension
    _renderDimension(dimension) {
        const simple = dimension.simple !== undefined && dimension.simple;
        let text = dimension.text !== undefined ? dimension.text.toString() : dimension.size.toString();
        text = text.replace('.', ',');
        const textOffset = dimension.textOffset !== undefined ? dimension.textOffset : 0;
        const { background, textCentered } = dimension;
        const dimensionSideLineIncrement = 0.6;
        if (dimension.vertical) {
            const bottomY = dimension.top + dimension.size;
            this._render.line({
                topX: dimension.left,
                topY: dimension.top,
                bottomX: dimension.left,
                bottomY: bottomY
            });
            if (!simple) {
                this._render.line({
                    topX: dimension.left - ((this._renderDimensionSpacing * dimensionSideLineIncrement) / 2) * 0.25,
                    topY: dimension.top,
                    bottomX: dimension.left + ((this._renderDimensionSpacing * dimensionSideLineIncrement) / 2),
                    bottomY: dimension.top
                });
                this._render.line({
                    topX: dimension.left - ((this._renderDimensionSpacing * dimensionSideLineIncrement) / 2) * 0.25,
                    topY: this.store.height,
                    bottomX: dimension.left + ((this._renderDimensionSpacing * dimensionSideLineIncrement) / 2),
                    bottomY: this.store.height
                });
            }
            this._render.polygon(this.arrow(dimension.top, dimension.left, 'top'), false);
            this._render.polygon(this.arrow(bottomY, dimension.left, 'bottom'), false);
            const textAngle = dimension.textRotate !== undefined && !dimension.textRotate ? 0 : 270;
            if (this.showLabels) {
                this._render.text({
                    top: dimension.top + (dimension.size / 2) + textOffset,
                    left: dimension.left,
                    text: text,
                    angle: textAngle,
                    isVertical: true,
                    fontSize: this._fontSize,
                    options: {
                        background: background,
                        textCentered: textCentered
                    }
                });
            }
        }
        else {
            this._render.line({
                topX: dimension.left,
                topY: dimension.top,
                bottomX: dimension.left + dimension.size,
                bottomY: dimension.top
            });
            if (!simple) {
                this._render.line({
                    topX: dimension.left,
                    topY: dimension.top - ((this._renderDimensionSpacing * dimensionSideLineIncrement) / 2),
                    bottomX: dimension.left,
                    bottomY: dimension.top + ((this._renderDimensionSpacing * dimensionSideLineIncrement) / 2) * 0.25
                });
                this._render.line({
                    topX: dimension.left + dimension.size,
                    topY: dimension.top - ((this._renderDimensionSpacing * dimensionSideLineIncrement) / 2),
                    bottomX: dimension.left + dimension.size,
                    bottomY: dimension.top + ((this._renderDimensionSpacing * dimensionSideLineIncrement) / 2) * 0.25
                });
            }
            this._render.polygon(this.arrow(dimension.top, dimension.left, 'left'), false);
            this._render.polygon(this.arrow(dimension.top, dimension.left + dimension.size, 'right'), false);
            if (this.showLabels) {
                this._render.text({
                    top: dimension.top + textOffset,
                    left: dimension.left + (dimension.size / 2),
                    text: text,
                    isVertical: dimension.vertical,
                    fontSize: this._fontSize,
                    options: {
                        background: background,
                    }
                });
            }
        }
    }
    // render doors
    _renderDoorsView() {
        const rectangleStrokeWidth = 4;
        const rectangleStrokeWidthRender = rectangleStrokeWidth / this.sizer;
        const imageScaleSize = this.imgIconScaleSize;
        const imageScaleRender = this.imgIconScaleSize;
        let doorNumber = 1;
        let drawerNumber = 1;
        this.store.doors.forEach((door) => {
            const y = this.store.height - (door.y + door.height + this.store.socketData.height);
            const x = door.x + this._leftDimensionsSpacingRenderSize;
            let doorMarkerTop = this.store.height - (door.y + this.store.socketData.height);
            const markLetter = door.type === 'drawer' ? 'L' : 'T';
            const markNumber = door.type === 'drawer' ? drawerNumber : doorNumber;
            // render left bevel front view markers
            let leftTextOffset = door.x + rectangleStrokeWidthRender + imageScaleRender / 2;
            let leftImageOffset = door.x + rectangleStrokeWidthRender;
            if (!this.store.isLeftBevel) {
                leftTextOffset = door.x + door.width - (rectangleStrokeWidthRender + imageScaleRender / 2);
                leftImageOffset = door.x + door.width - (rectangleStrokeWidthRender + imageScaleRender);
            }
            if (!door.underBevel) {
                this._render.rectangle({
                    top: y,
                    left: x,
                    width: door.width - rectangleStrokeWidthRender,
                    height: door.height - rectangleStrokeWidthRender,
                }, false, rectangleStrokeWidth, false, 'transparent');
            }
            else {
                this._renderBevelDoor(door, rectangleStrokeWidth);
            }
            // left bevel marker text
            if (this.showLabels) {
                this._render.text({
                    top: doorMarkerTop - (2 * imageScaleRender),
                    left: leftTextOffset,
                    isVertical: false,
                    text: `${markLetter}${markNumber}`,
                    fontSize: this._fontSize,
                    letterSpacing: -100,
                    options: {
                        backgroundColor: 'white'
                    }
                });
            }
            // left bevel marker image
            this._render.doorImage({
                top: doorMarkerTop - (imageScaleRender + rectangleStrokeWidthRender),
                left: leftImageOffset,
                angle: 0,
                opacity: 1,
                scale: imageScaleSize,
                type: door.type
            });
            if (door.type === 'drawer') {
                drawerNumber++;
            }
            else {
                doorNumber++;
            }
        });
    }
    // render door under bevel
    _renderBevelDoor(door, rectangleStrokeWidth) {
        const sinus = this.sinus;
        const offset = this.store.isLeftBevel ? door.x : this.store.width - (door.x);
        const offset2 = this.store.isLeftBevel ? door.x + door.width : this.store.width - (door.x + door.width);
        const height = this.store.aroundThickness / sinus;
        const h = Math.sqrt(Math.pow(offset / sinus, 2) - Math.pow(offset, 2)) + this.store.bevelHeight;
        const h2 = Math.sqrt(Math.pow(offset2 / sinus, 2) - Math.pow(offset2, 2)) + this.store.bevelHeight;
        const x1 = door.x + this._leftDimensionsSpacingRenderSize;
        const x2 = door.x + door.width + this._leftDimensionsSpacingRenderSize;
        const rectangleStrokeWidthRender = rectangleStrokeWidth / this.sizer;
        // top
        this._render.line({
            bottomX: x1,
            bottomY: (this.store.height - h) + height - (this.store.isLeftBevel ? (rectangleStrokeWidthRender / 2) : 0),
            topX: x2 - rectangleStrokeWidthRender,
            topY: (this.store.height - h2) + height + (this.store.isLeftBevel ? (rectangleStrokeWidthRender / 2) : 0),
        }, rectangleStrokeWidth);
        // left
        this._render.line({
            bottomX: x1,
            bottomY: (this.store.height - h) + height + (this.store.isLeftBevel ? 0 : rectangleStrokeWidthRender / 2),
            topX: x1,
            topY: this.store.height - (door.y + door.height + this.store.socketData.height) + door.height - (this.store.isLeftBevel ? 0 : rectangleStrokeWidthRender)
        }, rectangleStrokeWidth);
        // right
        this._render.line({
            bottomX: x2 - rectangleStrokeWidthRender,
            bottomY: (this.store.height - h2) + height + (!this.store.isLeftBevel ? 0 : rectangleStrokeWidthRender / 2),
            topX: x2 - rectangleStrokeWidthRender,
            topY: this.store.height - (door.y + door.height + this.store.socketData.height) + door.height - rectangleStrokeWidthRender
        }, rectangleStrokeWidth);
        // bottom
        this._render.line({
            bottomX: x1,
            bottomY: this.store.height - (door.y + door.height + this.store.socketData.height) + door.height - rectangleStrokeWidthRender,
            topX: x2,
            topY: this.store.height - (door.y + door.height + this.store.socketData.height) + door.height - rectangleStrokeWidthRender
        }, rectangleStrokeWidth);
    }
    // Render inner options
    _renderInsideOptions() {
        for (const insideOption of this.store.insideOptions) {
            // Split inside option type
            if (insideOption.type === InsideOptionType.Split) {
                const left = insideOption.left + ((insideOption.width - 2) / 2);
                const rectangle = Object.assign({}, insideOption, { left: left, width: 2 });
                this._renderInsideOptionRectangle(rectangle);
            }
            // Rail inside option type
            else if (insideOption.type === InsideOptionType.Rail) {
                const top = insideOption.top + TOP_INSIDE_OPTION_OFFSET;
                const rectangle = Object.assign({}, insideOption, { top: top, height: 2 });
                this._renderInsideOptionRectangle(rectangle);
            }
            // Pantograph inside option
            else if (insideOption.type === InsideOptionType.Pantograph) {
                this._renderPantograph(insideOption);
            }
            // Drawer inside option type
            else if (insideOption.type === InsideOptionType.Drawer) {
                this._renderDrawers(insideOption);
                if (insideOption.door) {
                    // Render overflow door rectangle
                    this.renderInsideOptionDrawerDoor(insideOption);
                    // Render door image for drawers
                    /* this.render.renderInsideOptionImage({
                         top: insideOption.top,
                         left: insideOption.left + insideOption.width,
                         type: insideOption.door.toString(),
                         scaleSize: this.imgIconScaleSize,
                         doorImage: true,
                     });*/
                }
            }
            else if (insideOption.type === InsideOptionType.LedLight) {
                this._renderLedLight(insideOption);
            }
            else if (insideOption.type === InsideOptionType.ClothesHolder) {
                this._renderClothesHolder(insideOption);
            }
            else if (insideOption.type === InsideOptionType.TrousersHolder) {
                this._renderTrousersHolder(insideOption);
            }
            else if (insideOption.type === InsideOptionType.BeltHolder) {
                this._renderBeltHolder(insideOption);
            }
            this.render.renderInsideOptionImage({
                top: insideOption.top + insideOption.height,
                left: this.store.isLeftBevel ? insideOption.left : insideOption.left + insideOption.width,
                type: insideOption.type,
                scaleSize: this.imgIconScaleSize,
                leftImage: this.store.isLeftBevel,
            });
        }
    }
    // Render rectangles for drawers under doors
    renderInsideOptionDrawerDoor(insideOption) {
        let rectangle = Object.assign({}, insideOption, { width: DRAWER_DOOR_SPACE });
        if (insideOption.door === DoorType.Right) {
            rectangle.left = rectangle.left + (insideOption.width - DRAWER_DOOR_SPACE);
        }
        this.render.rectangle(rectangle, false, 1, true, 'white', true);
        if (insideOption.door === DoorType.Both) {
            rectangle.left = rectangle.left + (insideOption.width - DRAWER_DOOR_SPACE);
            this.render.rectangle(rectangle, false, 1, true, 'white', true);
        }
    }
    // Render inside option split type
    _renderInsideOptionRectangle(rectangle, color = 'black') {
        this.render.rectangle(rectangle, false, 1, false, color);
    }
    // Render inside option pantograph type
    _renderPantograph(insideOption) {
        const top = insideOption.top + TOP_INSIDE_OPTION_OFFSET;
        const left = insideOption.left + PANTOGRAPH_SETTINGS.leftOffset;
        const pantographWidth = (insideOption.width - (2 * PANTOGRAPH_SETTINGS.leftOffset));
        // Pantograph rectangles
        const pantograph = [
            // left rod
            {
                top: top,
                left: left,
                height: PANTOGRAPH_SETTINGS.height,
                width: PANTOGRAPH_SETTINGS.rodWidth,
            },
            // right rod
            {
                top: top,
                left: insideOption.left + (insideOption.width - (PANTOGRAPH_SETTINGS.leftOffset + PANTOGRAPH_SETTINGS.rodWidth)),
                height: PANTOGRAPH_SETTINGS.height,
                width: PANTOGRAPH_SETTINGS.rodWidth,
            },
            // top rod
            {
                top: top,
                left: left,
                height: PANTOGRAPH_SETTINGS.rodWidth,
                width: pantographWidth,
            },
            // left holder
            {
                top: top + (PANTOGRAPH_SETTINGS.height - PANTOGRAPH_SETTINGS.holderHeight),
                left: insideOption.left,
                height: PANTOGRAPH_SETTINGS.holderHeight,
                width: PANTOGRAPH_SETTINGS.holderWidth,
            },
            // right holder
            {
                top: top + (PANTOGRAPH_SETTINGS.height - PANTOGRAPH_SETTINGS.holderHeight),
                left: left + insideOption.width - (PANTOGRAPH_SETTINGS.holderWidth + PANTOGRAPH_SETTINGS.leftOffset),
                height: PANTOGRAPH_SETTINGS.holderHeight,
                width: PANTOGRAPH_SETTINGS.holderWidth,
            },
            // middle rod
            {
                top: top,
                left: left + ((pantographWidth / 2) - (PANTOGRAPH_SETTINGS.rodWidth / 2)),
                width: PANTOGRAPH_SETTINGS.rodWidth,
                height: PANTOGRAPH_SETTINGS.height,
            }
        ];
        this._renderInsideOptionRectangles(pantograph);
    }
    // Render inside option drawer type
    _renderDrawers(insideOption) {
        const drawerCount = Math.floor(insideOption.height / DRAWER_HEIGHT);
        const deskHeight = (insideOption.height - (drawerCount * DRAWER_SPACE_BETWEEN_DESKS)) / drawerCount;
        let drawerRectangles = [];
        let topDrawersOffset = insideOption.top + insideOption.height;
        for (let i = drawerCount; i > 0; i--) {
            topDrawersOffset = topDrawersOffset - deskHeight;
            drawerRectangles.push({
                top: topDrawersOffset,
                left: insideOption.left,
                height: deskHeight,
                width: insideOption.width,
            });
            topDrawersOffset = topDrawersOffset - DRAWER_SPACE_BETWEEN_DESKS;
        }
        this._renderInsideOptionRectangles(drawerRectangles, false);
    }
    // Render inside option led light
    _renderLedLight(insideOption) {
        const width = 40;
        const left = insideOption.left + ((insideOption.width - 40) / 2);
        const rectangle = Object.assign({}, insideOption, { left,
            width, height: 2 });
        this._renderInsideOptionRectangle(rectangle, 'white');
        const lineCount = 13;
        for (let i = 0; i < lineCount; i++) {
            const x = left + i * width / (lineCount - 1);
            const diffFromMiddle = x - left - width / 2;
            const line = {
                topX: x,
                bottomX: x + diffFromMiddle / 15,
                topY: insideOption.top + 3,
                bottomY: insideOption.top + 4.5,
            };
            this._render.line(line, 0.5);
        }
    }
    // Render inside option clothes holder
    _renderClothesHolder(insideOption) {
        const top = insideOption.top + insideOption.height - 30;
        const rectangleTop = Object.assign({}, insideOption, { top, width: 6, height: 2 });
        this._renderInsideOptionRectangle(rectangleTop);
        const rectangleBottom = Object.assign({}, insideOption, { top, left: insideOption.left + 2, width: 2, height: 8 });
        this._renderInsideOptionRectangle(rectangleBottom);
    }
    // Render inside option trousers holder
    _renderTrousersHolder(insideOption) {
        const top = insideOption.top + insideOption.height - 45;
        const rectangleLeft = Object.assign({}, insideOption, { top, width: 2, height: 7 });
        this._renderInsideOptionRectangle(rectangleLeft);
        const rectangleLong = Object.assign({}, insideOption, { top: top + 2.5, width: 22, height: 2 });
        this._renderInsideOptionRectangle(rectangleLong);
    }
    // Render inside option belt holder
    _renderBeltHolder(insideOption) {
        const top = insideOption.top + insideOption.height - 25;
        const left = insideOption.left + insideOption.width;
        const rectangleRing = Object.assign({}, insideOption, { top: top + 1.8, left: left - 8, width: 6, height: 2.6 });
        this._renderInsideOptionRectangle(rectangleRing, 'white');
        const rectangleRight = Object.assign({}, insideOption, { top, left: left - 2, width: 2, height: 6 });
        this._renderInsideOptionRectangle(rectangleRight);
        const rectangleBottom = Object.assign({}, insideOption, { top: top + 6, left: left - 7, width: 7, height: 0.5 });
        this._renderInsideOptionRectangle(rectangleBottom);
    }
    // Render rectangles
    _renderInsideOptionRectangles(rectangles, fillBackground = true) {
        for (const rectangle of rectangles) {
            this.render.rectangle(rectangle, false, 1, false, 'black', fillBackground);
        }
    }
}
//# sourceMappingURL=MeasurementView.js.map